import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Icon, IconWrapper } from './primitives';
import { hapticSelection } from '../utils/haptics';

/**
 * BottomNav — bottom navigation of the shell.
 *
 * Figma source: updated bottom nav reference (2026-06) — four equal glass tabs:
 *   Наборы / AI Фильтры / Создать / Галерея.
 *
 * Geometry — NONE invented here; all visuals come from primitives:
 *   - <Navbar>     — glass-16 pill, backdrop-blur 80, cornerRadius 48,
 *                    gradient stroke (see primitives/Navbar.jsx).
 *   - <Icon>       — real Figma SVG from /public/icons/ (no <path d>).
 *   - <IconWrapper>— 24×24 flex container for the icon.
 *
 * About MenuItem: the primitive is a <button>. Tabs must render as links
 * (for Telegram history + test contract `getByRole('link', {...})`). We
 * therefore compose a `<NavLink>` with the same visual geometry the
 * MenuItem primitive documents (VERTICAL, center/center, pad 4, gap 2,
 * rounded-full) — using the same token classes (text-menu-label, coin-yellow,
 * text-text-secondary). No geometry is invented; we mirror the primitive.
 *
 * Positioning (not part of the primitive):
 *  - Fixed to viewport bottom with transparent→bg-app fade behind the pill
 *    so content scrolled under it stays legible. Container padding honours
 *    safe-area-bottom.
 */

// Shared tab geometry for the 4-item glass navbar. Kept as a function so all
// tabs render identically.
function NavTab({ to, end, iconName, label, isActive }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={() => {
        if (!isActive) hapticSelection();
      }}
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
      className={[
        // Figma menuItem layoutGrow=1 — растягиваем таб на свободное место.
        'relative z-10 min-w-0 pointer-events-auto select-none',
        'h-12 flex-1 flex flex-col items-center justify-center',
        'gap-0.5 px-1 py-1 rounded-full',
      ].join(' ')}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
    >
      <IconWrapper size={24}>
        <Icon
          name={iconName}
          size={24}
          className={isActive ? 'text-coin-yellow' : 'text-text-secondary'}
        />
      </IconWrapper>
      <span
        className={[
          'w-full truncate text-center font-sans text-[12px] leading-[14px] font-bold',
          isActive ? 'text-text-primary' : 'text-text-secondary',
        ].join(' ')}
      >
        {label}
      </span>
    </NavLink>
  );
}

export default function BottomNav() {
  const location = useLocation();
  const { t } = useTranslation();

  const isHome = location.pathname === '/';
  const isFilters =
    location.pathname.startsWith('/photo-catalog') ||
    location.pathname.startsWith('/video-catalog');
  const isHomeRubricDetailPage = location.pathname.startsWith('/photo-catalog/section/');
  const isCreate = location.pathname.startsWith('/prompt');
  const isGallery = location.pathname === '/gallery';
  const isVisible =
    !isHomeRubricDetailPage && (isHome || isFilters || isCreate || isGallery);
  const activeIndex = isFilters ? 1 : isCreate ? 2 : isGallery ? 3 : 0;
  const labels = {
    aria: t('bottomNav.aria', 'Основная навигация'),
    home: t('bottomNav.home', 'Наборы'),
    filters: t('bottomNav.filters', 'AI Фильтры'),
    create: t('bottomNav.create', 'Создать'),
    gallery: t('bottomNav.gallery', 'Галерея'),
  };

  return (
    <div
      className={[
        'app-bottom-nav fixed bottom-0 left-0 right-0 z-40 isolate px-2 pt-4 pointer-events-auto',
        'transition-[opacity,transform] duration-150 ease-out',
        isVisible ? 'opacity-100 translate-y-0' : 'pointer-events-none translate-y-full opacity-0',
      ].join(' ')}
      aria-hidden={!isVisible}
      style={{
        bottom: 'var(--nav-bottom-offset, 0px)',
        // Transparent → bg-app (surface-1 = #010103) fade behind the pill.
        // No invented alpha; uses the same bg-app color as the app shell.
        background:
          'linear-gradient(to bottom, rgba(1,1,3,0) 0%, rgba(1,1,3,1) 60%)',
        paddingBottom:
          'calc(var(--safe-bottom, 0px) + var(--nav-bottom-gap, 12px))',
        touchAction: 'manipulation',
      }}
    >
      <nav className="mx-auto w-full max-w-[430px] pointer-events-auto" aria-label={labels.aria}>
        <Navbar innerClassName="h-12 px-1 gap-0 overflow-hidden">
          <span
            aria-hidden="true"
            className="absolute top-0 bottom-0 left-1 rounded-full bg-white/[0.08] transition-transform duration-300 ease-out will-change-transform"
            style={{
              width: 'calc((100% - 8px) / 4)',
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />
          <NavTab to="/" end iconName="packs" label={labels.home} isActive={isHome} />
          <NavTab
            to="/photo-catalog"
            iconName="ai-filters"
            label={labels.filters}
            isActive={isFilters}
          />
          <NavTab
            to="/prompt"
            iconName="create-nav"
            label={labels.create}
            isActive={isCreate}
          />
          <NavTab
            to="/gallery"
            iconName="gallery-nav"
            label={labels.gallery}
            isActive={isGallery}
          />
        </Navbar>
      </nav>
    </div>
  );
}
